import React from 'react'
import PropTypes from 'prop-types'
import { withUserStore } from '../../contexts/UserStore'

const PrioritiesStrategyGoalsPrimer = ({ accountType = 'business', variant = 'tour', userStore }) => {
  // Get dynamic account type from userStore if available, fall back to prop
  const getCurrentAccountType = () => {
    if (userStore?.account?.accountType) {
      return userStore.account.accountType
    }
    return accountType
  }
  
  const dynamicAccountType = getCurrentAccountType()
  
  // Adapt content based on account type
  const getAccountTypeText = () => {
    switch (dynamicAccountType) {
      case 'business':
        return {
          context: 'organization',
          teamText: 'your team',
          priorityScope: 'your organization',
          strategyActor: 'your team',
          goalActor: 'an individual does',
          examplePriority: 'Customer Excellence',
          exampleStrategy: 'Customer Feedback Loop',
          exampleGoal: '95% Customer Satisfaction'
        }
      case 'team':
        return {
          context: 'team',
          teamText: 'the team',
          priorityScope: 'your team',
          strategyActor: 'your team',
          goalActor: 'an individual does',
          examplePriority: 'Quality First',
          exampleStrategy: 'Code Review Process',
          exampleGoal: 'Zero Critical Bugs'
        }
      case 'personal':
        return {
          context: 'personal growth',
          teamText: 'you',
          priorityScope: 'you',
          strategyActor: 'you',
          goalActor: 'you do',
          examplePriority: 'Continuous Learning',
          exampleStrategy: 'Weekly Learning Time',
          exampleGoal: 'Complete 3 Courses This Quarter'
        }
      default:
        return {
          context: 'organization',
          teamText: 'your team',
          priorityScope: 'your organization',
          strategyActor: 'your team',
          goalActor: 'an individual does',
          examplePriority: 'Customer Excellence',
          exampleStrategy: 'Customer Feedback Loop',
          exampleGoal: '95% Customer Satisfaction'
        }
    }
  }

  const { priorityScope, strategyActor, goalActor, examplePriority, exampleStrategy, exampleGoal } = getAccountTypeText()

  if (variant === 'full') {
    // Full version for help pages
    return (
      <div className="priorities-primer-full">
        <h2>Understanding Priorities, Strategies, and Goals</h2>
        
        <div className="row mt-4">
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-body">
                <h3 className="card-title text-primary">
                  <i className="fas fa-star me-2"></i>
                  Priorities
                </h3>
                <p className="card-text">
                  <strong>What:</strong> What matters to {priorityScope}
                </p>
                <p className="card-text">
                  <strong>Scope:</strong> Small set across the organization
                </p>
                <p className="card-text">
                  <strong>Lifecycle:</strong> Rarely change, if at all
                </p>
                <p className="card-text text-muted">
                  <strong>Example:</strong> &quot;{examplePriority}&quot;
                </p>
              </div>
            </div>
          </div>
          
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-body">
                <h3 className="card-title text-success">
                  <i className="fas fa-chess me-2"></i>
                  Strategies
                </h3>
                <p className="card-text">
                  <strong>What:</strong> How {strategyActor} approaches each priority
                </p>
                <p className="card-text">
                  <strong>Scope:</strong> Each team has 1+ strategies per priority
                </p>
                <p className="card-text">
                  <strong>Lifecycle:</strong> Updated infrequently (~5 year cycle)
                </p>
                <p className="card-text text-muted">
                  <strong>Example:</strong> &quot;{exampleStrategy}&quot;
                </p>
              </div>
            </div>
          </div>
          
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-body">
                <h3 className="card-title text-info">
                  <i className="fas fa-bullseye me-2"></i>
                  Goals
                </h3>
                <p className="card-text">
                  <strong>What:</strong> What {goalActor} to implement each strategy
                </p>
                <p className="card-text">
                  <strong>Scope:</strong> Each individual has 1+ goals per team strategy
                </p>
                <p className="card-text">
                  <strong>Lifecycle:</strong> Reevaluated annually
                </p>
                <p className="card-text text-muted">
                  <strong>Example:</strong> &quot;{exampleGoal}&quot;
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-light rounded">
          <h4>How They Work Together</h4>
          <p className="mb-2">
            <strong>Priorities</strong> - Small set across organization, rarely change
          </p>
          <p className="mb-2">
            <strong>Strategies</strong> - Each team has 1+ per priority, ~5 year cycle
          </p>
          <p className="mb-2">
            <strong>Goals</strong> - Each individual has 1+ per strategy, annual reevaluation
          </p>
          <div className="text-center mt-3">
            <code>Organization → Teams → Individuals</code>
          </div>
        </div>
      </div>
    )
  }

  // Tour version (compact)
  return (
    <div className="priorities-primer-tour">
      <h4>The Prioritize.Plus Framework 🎯</h4>
      
      <p>Prioritize.Plus helps you accomplish what matters most using three hierarchical concepts:</p>
      
      <div className="mt-3">
        <div className="d-flex align-items-start mb-2">
          <span className="badge bg-primary me-2" style={{ minWidth: '25px' }}>1</span>
          <div>
            <strong>Priorities</strong> - What matters to {priorityScope}
            <br />
            <small className="text-muted">Example: {examplePriority}</small>
          </div>
        </div>
        
        <div className="d-flex align-items-start mb-2">
          <span className="badge bg-success me-2" style={{ minWidth: '25px' }}>2</span>
          <div>
            <strong>Strategies</strong> - How {strategyActor} approaches each priority
            <br />
            <small className="text-muted">Example: {exampleStrategy}</small>
          </div>
        </div>
        
        <div className="d-flex align-items-start mb-2">
          <span className="badge bg-info me-2" style={{ minWidth: '25px' }}>3</span>
          <div>
            <strong>Goals</strong> - What {goalActor} to implement each strategy
            <br />
            <small className="text-muted">Example: {exampleGoal}</small>
          </div>
        </div>
      </div>
      
    </div>
  )
}

PrioritiesStrategyGoalsPrimer.propTypes = {
  accountType: PropTypes.oneOf(['business', 'team', 'personal']),
  variant: PropTypes.oneOf(['tour', 'full']),
  userStore: PropTypes.object
}

export default withUserStore(PrioritiesStrategyGoalsPrimer)